/**
 * The twenty-two columns of the candidate register.
 *
 * Data only, no JSX. The column chooser needs the list without pulling in every cell
 * renderer, and a plain array can be unit-tested.
 *
 * Eight are on by default, the set named in the plan (Phase 13): received, name,
 * status, contacted, Flyst screening, RSR screening, points, preferred country. The
 * other fourteen are a click away in the chooser.
 */

import type { SortDirection } from '@/components/ui/Table';

export type CandidateColumnKey =
  | 'received_on'
  | 'full_name'
  | 'status'
  | 'contacted'
  | 'flyst_stage'
  | 'rsr_state'
  | 'sponsor_points'
  | 'preferred_country'
  | 'reference'
  | 'details_shared'
  | 'followup_done'
  | 'documentation'
  | 'location'
  | 'phone'
  | 'email'
  | 'source'
  | 'applying_for'
  | 'department'
  | 'preferred_position'
  | 'rsr_scheduled_on'
  | 'cv'
  | 'payment_status';

/**
 * The sort values the API accepts.
 *
 * It answers anything else with a 422 rather than falling back quietly, so offering a
 * sort the server does not know would be a header that visibly breaks the screen.
 *
 * `created_at` is accepted by the API and deliberately not offered: it is not a field
 * on the row, and re-ordering a table by a value nobody can see reads as a bug.
 */
export const SORTABLE_KEYS = [
  'received_on',
  'full_name',
  'status',
  'sponsor_points',
  'reference',
  'rsr_state',
] as const;

export type CandidateSortKey = (typeof SORTABLE_KEYS)[number];

export const DEFAULT_SORT_BY: CandidateSortKey = 'received_on';
export const DEFAULT_SORT_DIR: SortDirection = 'desc';

export type CandidateColumn = {
  key: CandidateColumnKey;
  label: string;
  /** Shown to someone who has never opened the chooser. */
  on: boolean;
  /** Cannot be hidden. Only the name: a row with no name is not a row. */
  locked?: true;
  /** Present only where the API can actually sort by it. */
  sortKey?: CandidateSortKey;
  /** Right-aligned, for numbers read down a column. */
  align?: 'end';
};

export const COLUMNS: readonly CandidateColumn[] = [
  { key: 'received_on', label: 'Received', on: true, sortKey: 'received_on' },
  { key: 'full_name', label: 'Name', on: true, locked: true, sortKey: 'full_name' },
  { key: 'status', label: 'Status', on: true, sortKey: 'status' },
  { key: 'contacted', label: 'Contacted', on: true },
  { key: 'flyst_stage', label: 'Flyst screening', on: true },
  { key: 'rsr_state', label: 'RSR screening', on: true, sortKey: 'rsr_state' },
  { key: 'sponsor_points', label: 'Points', on: true, sortKey: 'sponsor_points', align: 'end' },
  { key: 'preferred_country', label: 'Preferred country', on: true },

  { key: 'reference', label: 'Reference', on: false, sortKey: 'reference' },
  { key: 'details_shared', label: 'Details shared', on: false },
  { key: 'followup_done', label: 'Follow-up', on: false },
  { key: 'documentation', label: 'Documentation', on: false },
  { key: 'location', label: 'Location', on: false },
  { key: 'phone', label: 'Contact no.', on: false },
  { key: 'email', label: 'Email', on: false },
  { key: 'source', label: 'Source', on: false },
  { key: 'applying_for', label: 'Applying for', on: false },
  { key: 'department', label: 'Department', on: false },
  { key: 'preferred_position', label: 'Preferred position', on: false },
  { key: 'rsr_scheduled_on', label: 'RSR scheduled', on: false },
  { key: 'cv', label: 'CV / Resume', on: false },
  { key: 'payment_status', label: 'Payment status', on: false },
];

export const DEFAULT_COLUMN_KEYS: CandidateColumnKey[] = COLUMNS.filter(
  (column) => column.on,
).map((column) => column.key);

export const COLUMN_KEYS: ReadonlySet<string> = new Set(COLUMNS.map((column) => column.key));

export const LOCKED_COLUMN_KEY: CandidateColumnKey = 'full_name';

/**
 * The visible columns, always in the order declared above.
 *
 * Never the order the user ticked them in: a table whose columns rearrange themselves
 * according to the sequence somebody clicked checkboxes is unreadable.
 */
export function visibleColumns(keys: readonly CandidateColumnKey[]): CandidateColumn[] {
  const wanted = new Set(keys);
  return COLUMNS.filter((column) => wanted.has(column.key));
}

/**
 * How wide the table needs before it scrolls sideways.
 *
 * A floor of 900 so a three-column table still matches the staff list rather than
 * looking like a different application, and 150px a column beyond that.
 */
export function tableMinWidth(visibleCount: number): number {
  return Math.max(900, visibleCount * 150);
}
